import React, { useState } from 'react';
import {
  Search,
  Lock,
  Target,
  Database,
  ChevronLeft,
  ChevronRight,
  Eye,
  Clock,
  AlertTriangle,
  BarChart3,
  Info
} from 'lucide-react';

/**
 * PhaseNavigator - Navigation component for EBIOS RM phases
 * Provides phase selection, progress tracking, and phase-specific information
 */
const PhaseNavigator = ({
  scenario,
  currentPhase,
  onPhaseChange,
  onVisualize,
  showVisualization = true,
  compact = false
}) => {
  const [showPhaseDetails, setShowPhaseDetails] = useState(null);

  // Extract phases from scenario
  const phases = scenario?.operationalSteps?.map(step => ({
    phase: step.phase,
    technique: step.technique,
    description: step.description,
    duration: step.duration,
    difficulty: step.difficulty,
    stepProbability: step.stepProbability,
    indicators: step.indicators || [],
    tools: step.tools || [],
    stepNumber: step.stepNumber
  })) || [];

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'CONNAITRE': return <Search className="h-4 w-4" />;
      case 'RENTRER': return <Lock className="h-4 w-4" />;
      case 'TROUVER': return <Target className="h-4 w-4" />;
      case 'EXPLOITER': return <Database className="h-4 w-4" />;
      default: return <Eye className="h-4 w-4" />;
    }
  };

  const getPhaseColor = (phase, isActive = false, isCompleted = false) => {
    const baseColors = {
      'CONNAITRE': isActive ? 'bg-blue-600 text-white' : isCompleted ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600',
      'RENTRER': isActive ? 'bg-orange-600 text-white' : isCompleted ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-600',
      'TROUVER': isActive ? 'bg-purple-600 text-white' : isCompleted ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600',
      'EXPLOITER': isActive ? 'bg-red-600 text-white' : isCompleted ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'
    };
    return baseColors[phase] || (isActive ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-600');
  };

  const getPhaseDescription = (phase) => {
    const descriptions = {
      'CONNAITRE': 'Reconnaissance et collecte d\'informations sur la cible',
      'RENTRER': 'Intrusion initiale et établissement de l\'accès',
      'TROUVER': 'Reconnaissance interne et mouvement latéral',
      'EXPLOITER': 'Actions sur objectifs et exfiltration'
    };
    return descriptions[phase] || 'Phase du scénario d\'attaque';
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Très faible': return 'text-green-600 bg-green-100';
      case 'Faible': return 'text-green-600 bg-green-100';
      case 'Moyen': return 'text-yellow-600 bg-yellow-100';
      case 'Élevé': return 'text-orange-600 bg-orange-100';
      case 'Très élevé': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const currentPhaseIndex = phases.findIndex(p => p.phase === currentPhase);
  const currentPhaseData = phases[currentPhaseIndex];

  const handlePrevious = () => {
    if (currentPhaseIndex > 0) {
      onPhaseChange(phases[currentPhaseIndex - 1].phase);
    }
  };

  const handleNext = () => {
    if (currentPhaseIndex < phases.length - 1) {
      onPhaseChange(phases[currentPhaseIndex + 1].phase);
    }
  };

  if (phases.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
          <span className="text-yellow-800 text-sm">Aucune phase EBIOS RM disponible pour ce scénario</span>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center space-x-2">
        {phases.map((phaseData, index) => {
          const isActive = phaseData.phase === currentPhase;
          const isCompleted = index < currentPhaseIndex;
          
          return (
            <button
              key={phaseData.phase}
              onClick={() => onPhaseChange(phaseData.phase)}
              className={`
                flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                ${getPhaseColor(phaseData.phase, isActive, isCompleted)}
                hover:shadow-md
              `}
            >
              {getPhaseIcon(phaseData.phase)}
              <span>{phaseData.phase}</span>
            </button>
          );
        })}
        
        {showVisualization && onVisualize && currentPhaseData && (
          <button
            onClick={() => onVisualize(currentPhase)}
            className="flex items-center space-x-1 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            <Eye className="h-4 w-4" />
            <span>Visualiser</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h4 className="text-lg font-semibold text-gray-900">Navigation des Phases EBIOS RM</h4>
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <span>Phase {currentPhaseIndex + 1} sur {phases.length}</span>
        </div>
      </div>

      {/* Phase Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center space-x-2">
          {phases.map((phaseData, index) => {
            const isActive = index === currentPhaseIndex;
            const isCompleted = index < currentPhaseIndex;
            
            return (
              <React.Fragment key={phaseData.phase}>
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => onPhaseChange(phaseData.phase)}
                    className={`
                      w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200
                      ${getPhaseColor(phaseData.phase, isActive, isCompleted)}
                      hover:shadow-lg transform hover:scale-105
                    `}
                  >
                    {getPhaseIcon(phaseData.phase)}
                  </button>
                  <span className="text-xs font-medium mt-2 text-center max-w-[60px]">
                    {phaseData.phase}
                  </span>
                </div>
                
                {index < phases.length - 1 && (
                  <div className={`flex-1 h-1 rounded-full mx-2 ${
                    index < currentPhaseIndex ? 'bg-green-400' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Current Phase Details */}
      {currentPhaseData && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                getPhaseColor(currentPhaseData.phase, true)
              }`}>
                {getPhaseIcon(currentPhaseData.phase)}
              </div>
              <div>
                <h5 className="font-semibold text-gray-900">{currentPhaseData.phase}</h5>
                <p className="text-sm text-gray-600">{getPhaseDescription(currentPhaseData.phase)}</p>
              </div>
            </div>
            
            {showVisualization && onVisualize && (
              <button
                onClick={() => onVisualize(currentPhase)}
                className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
              >
                <Eye className="h-4 w-4" />
                <span>Visualiser Phase</span>
              </button>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <span className="text-sm font-medium text-gray-700">Technique:</span>
              <p className="text-sm text-gray-900">{currentPhaseData.technique}</p>
            </div>
            
            <div>
              <span className="text-sm font-medium text-gray-700">Description:</span>
              <p className="text-sm text-gray-900">{currentPhaseData.description}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {currentPhaseData.duration && (
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <div>
                    <div className="text-xs text-gray-600">Durée</div>
                    <div className="text-sm font-medium">{currentPhaseData.duration}</div>
                  </div>
                </div>
              )}
              
              {currentPhaseData.difficulty && (
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="h-4 w-4 text-gray-500" />
                  <div>
                    <div className="text-xs text-gray-600">Difficulté</div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getDifficultyColor(currentPhaseData.difficulty)}`}>
                      {currentPhaseData.difficulty}
                    </span>
                  </div>
                </div>
              )}
              
              {currentPhaseData.stepProbability && (
                <div className="flex items-center space-x-2">
                  <BarChart3 className="h-4 w-4 text-gray-500" />
                  <div>
                    <div className="text-xs text-gray-600">Probabilité</div>
                    <div className="text-sm font-medium">{currentPhaseData.stepProbability}%</div>
                  </div>
                </div>
              )}
              
              <div className="flex items-center space-x-2">
                <Info className="h-4 w-4 text-gray-500" />
                <div>
                  <div className="text-xs text-gray-600">Éléments</div>
                  <div className="text-sm font-medium">
                    {(currentPhaseData.indicators?.length || 0) + (currentPhaseData.tools?.length || 0)} items
                  </div>
                </div>
              </div>
            </div>

            {/* Indicators and Tools */}
            {(currentPhaseData.indicators?.length > 0 || currentPhaseData.tools?.length > 0) && (
              <div className="pt-3 border-t border-gray-200">
                {currentPhaseData.indicators?.length > 0 && (
                  <div className="mb-3">
                    <div className="text-xs font-medium text-gray-600 mb-2">Indicateurs ({currentPhaseData.indicators.length}):</div>
                    <div className="flex flex-wrap gap-1">
                      {currentPhaseData.indicators.slice(0, 3).map((indicator, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          {typeof indicator === 'string' ? indicator : indicator.indicator || indicator.name}
                        </span>
                      ))}
                      {currentPhaseData.indicators.length > 3 && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          +{currentPhaseData.indicators.length - 3} autres
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                {currentPhaseData.tools?.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-gray-600 mb-2">Outils ({currentPhaseData.tools.length}):</div>
                    <div className="flex flex-wrap gap-1">
                      {currentPhaseData.tools.slice(0, 3).map((tool, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                          {typeof tool === 'string' ? tool : tool.name || tool.tool}
                        </span>
                      ))}
                      {currentPhaseData.tools.length > 3 && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          +{currentPhaseData.tools.length - 3} autres
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navigation Controls */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentPhaseIndex === 0}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Phase Précédente</span>
        </button>

        <div className="text-sm text-gray-600">
          {currentPhaseData?.phase} ({currentPhaseIndex + 1}/{phases.length})
        </div>

        <button
          onClick={handleNext}
          disabled={currentPhaseIndex === phases.length - 1}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <span>Phase Suivante</span>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default PhaseNavigator;
